import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiHeart, FiShare2, FiCalendar, FiMessageSquare, FiDollarSign, FiTruck, FiShield, FiCheckCircle } = FiIcons;

function VehicleModal({ vehicle, isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLiked, setIsLiked] = useState(false);

  if (!vehicle) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'features', label: 'Features' },
    { id: 'financing', label: 'Financing' },
    { id: 'history', label: 'History' }
  ];

  const vehicleFeatures = [
    'Quattro All-Wheel Drive',
    'Premium Plus Package',
    'Virtual Cockpit Display',
    'MMI Navigation Plus',
    'Audi Pre Sense Safety',
    'LED Headlights',
    'Heated Seats',
    'Apple CarPlay/Android Auto',
    'Bang & Olufsen Sound',
    'Panoramic Sunroof'
  ];

  const handleScheduleTest = () => {
    // This would integrate with your scheduling system
    alert('Test drive scheduled! You will receive a confirmation email shortly.');
  };

  const handleGetFinancing = () => {
    // This would open financing application
    alert('Redirecting to financing application...');
  };

  const handleTradeInquiry = () => {
    // This would open trade-in estimator
    alert('Opening trade-in estimator...');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative">
              <img
                src={vehicle.image}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="w-full h-64 object-cover"
              />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                <div className="bg-white/90 rounded-lg p-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h2>
                  <p className="text-gray-600">{vehicle.mileage?.toLocaleString()} km</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsLiked(!isLiked)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isLiked ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-600'
                    }`}
                  >
                    <SafeIcon icon={FiHeart} className="w-5 h-5" />
                  </button>
                  <button className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors">
                    <SafeIcon icon={FiShare2} className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Price and Quick Actions */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 space-y-4 md:space-y-0">
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    ${vehicle.price?.toLocaleString()} CAD
                  </div>
                  <div className="text-sm text-gray-500">Est. $787/month CAD</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleScheduleTest}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>Schedule Test Drive</span>
                  </button>
                  <button
                    onClick={handleGetFinancing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                    <span>Get Financing</span>
                  </button>
                  <button
                    onClick={handleTradeInquiry}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiTruck} className="w-4 h-4" />
                    <span>Trade Inquiry</span>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <div className="flex space-x-8">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-2 border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="max-h-64 overflow-y-auto">
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold mb-2">Vehicle Details</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Year:</span>
                            <span>{vehicle.year}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Make:</span>
                            <span>{vehicle.make}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Model:</span>
                            <span>{vehicle.model}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Mileage:</span>
                            <span>{vehicle.mileage?.toLocaleString()} km</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Transmission:</span>
                            <span>8-Speed Automatic</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Engine:</span>
                            <span>2.0L Turbo</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Halifax Highlights</h3>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                            <span className="text-sm">Perfect for Maritime winters</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiShield} className="w-4 h-4 text-green-500" />
                            <span className="text-sm">Comprehensive warranty</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiTruck} className="w-4 h-4 text-green-500" />
                            <span className="text-sm">Halifax delivery available</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'features' && (
                  <div>
                    <h3 className="font-semibold mb-4">Standard Features</h3>
                    <div className="grid md:grid-cols-2 gap-2">
                      {vehicleFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'financing' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold mb-2">Halifax Financing Options</h3>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="font-medium">Purchase Financing</div>
                          <div>From 4.9% APR</div>
                          <div>Up to 84 months</div>
                        </div>
                        <div>
                          <div className="font-medium">Lease Options</div>
                          <div>From $599/month CAD</div>
                          <div>24-48 month terms</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Pre-Approval Benefits</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Know your Halifax budget instantly</li>
                        <li>• Negotiate with confidence</li>
                        <li>• Streamlined paperwork</li>
                        <li>• Same-day approval available</li>
                      </ul>
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div className="space-y-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-semibold mb-2 flex items-center space-x-2">
                        <SafeIcon icon={FiShield} className="w-5 h-5 text-green-600" />
                        <span>Clean Halifax History</span>
                      </h3>
                      <div className="text-sm space-y-2">
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                          <span>No accidents reported</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                          <span>Regular Maritime maintenance</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                          <span>Single Nova Scotia owner</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                          <span>Complete service records</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* AI Assistant */}
              <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">Ask Halifax AI</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Have questions about this vehicle? Our Halifax AI can help with comparisons, financing, or Maritime driving conditions.
                </p>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                  Chat with AI About This Vehicle
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default VehicleModal;